// components/Navbar.tsx
import React, { useEffect, useState, useCallback } from 'react';
import useFruitStore from '../../store/fruits';
import useStoreAllCats from '../../store/catSUBCat/cat';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguageStore } from '../../routes/lang';
import useCartDetail from '../../store/cart/getcartDetails';
import { useTranslation } from 'react-i18next';
import { CircleUser, ShoppingCart, Search, X } from 'lucide-react';
import NavItem from './NavItem';
import NavDropdown from './NavDropdown';
import Beancur from './Beancur';

const Navbar = ({ isSidebarOpen, toggleSidebar, isShrunk }: any) => {
  const { fetchAllFruit, searchFruits } = useFruitStore();
  const token = localStorage.getItem("token");
  const [query, setQuery] = useState<string>("");
  const userId = localStorage.getItem("user");
  const [connect, setConnect] = useState(token);
  const [showSearch, setShowSearch] = useState(false);
  const { AllCats, fetchAllCats } = useStoreAllCats();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setLanguage } = useLanguageStore();
  const { cartDetails, fetchAllCat } = useCartDetail((state) => state);

  // Mémoriser les fonctions fetch pour éviter les re-rendus inutiles
  const fetchData = useCallback(() => {
    fetchAllFruit();
    fetchAllCats();
    fetchAllCat();
  }, [fetchAllFruit, fetchAllCats, fetchAllCat]);

  useEffect(() => {
    setConnect(token);
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    searchFruits(value);
  };

  const clearSearch = () => {
    setQuery("");
    searchFruits("");
    setShowSearch(false);
  };

  const number = cartDetails?.data ? cartDetails.data.length : 0;

  const gotToCart = () => {
    if (!token) {
      // Au lieu de rediriger, montrer un message ou modal
      const shouldLogin = window.confirm(t("cart.loginRequired") || "Veuillez vous connecter pour accéder au panier");
      if (shouldLogin) {
        navigate("/signup");
      }
    } else {
      navigate("/cart");
    }
  };

  // Configuration des menus déroulants
  const servicesItems = [
    { label: t("recherche_transporteur"), path: "/recherche_trans" },
    { label: t("stock_produit"), path: "/comming_stock", state: "/assets/Stock_image.jpg" },
    { label: t("info_clim"), path: "/comming_meteo" },
  ];

  const ordersItems = [
    { label: t("quotation.myRequests"), path: "/quotation_list" },
    { label: t("quotation.commandes"), path: "/commandes" },
  ];

  const languageItems = [
    { label: "Français", onClick: () => setLanguage("fr") },
    { label: "English", onClick: () => setLanguage("en") },
  ];

  const userMenuItems = [
    { 
      label: t("logout"), 
      onClick: () => {
        localStorage.removeItem("tokenFire");
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        setConnect(null);
      }
    },
  ];

  return (
    <header 
      className={`bg-gray-100 text-black ${isShrunk ? "shadow-md" : "shadow-none"} fixed w-full z-50`}
      role="banner"
    >
      <Beancur />
      
      <div 
        className={`${isShrunk ? 'navbar-shrink py-1' : 'py-2'} w-full`}
        style={{ backgroundColor: isShrunk ? 'white' : '#157A3B' }}
      >
        <div className="container mx-auto px-4 flex items-center justify-between">
          
          {/* Logo et bouton mobile */}
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleSidebar}
              className="flex lg:hidden focus:outline-none focus:ring-2 focus:ring-white rounded"
              aria-label={t("toggleMenu") || "Menu"}
            >
              <svg className="w-6 h-6" fill="none" stroke={isShrunk ? "black" : "white"} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <Link to="/" className="flex-shrink-0" aria-label="Accueil">
              <img 
                className="h-[40px] w-auto md:h-[50px] object-contain" 
                src={isShrunk ? "/assets/logo_black.png" : "/assets/logo_white.png"} 
                alt="Logo" 
              />
            </Link>
          </div>

          {/* Navigation principale */}
          <nav className="flex-1 ml-4 lg:ml-8" role="navigation" aria-label="Navigation principale">
            <ul className="flex items-center justify-end space-x-2 md:space-x-4 lg:space-x-6">
              
              {/* Liens principaux - Desktop */}
              <li className="hidden lg:flex">
                <NavItem to="/" label={t("home")} isShrunk={isShrunk} />
              </li>
              <li className="hidden lg:flex">
                <NavItem to="/list_products" label={t("produits")} isShrunk={isShrunk} />
              </li>
              <li className="hidden lg:flex text-center">
                <NavItem to="/consulter/" label={t("prix_marcher")} isShrunk={isShrunk} />
              </li>
              <li className="hidden lg:flex text-center">
                <NavItem to="/recherche_trans/" label={t("recherche_transporteur")} isShrunk={isShrunk} />
              </li>
              <li className="hidden lg:flex text-center">
                <NavItem to="/comming_stock/" label={t("stock_produit")} isShrunk={isShrunk} />
              </li>
              <li className="hidden lg:flex text-center">
                <NavItem to="/comming_meteo/" label={t("info_clim")} isShrunk={isShrunk} />
              </li>
              
              {/* Services - Dropdown */}
              {/* <li className="hidden md:flex">
                <NavDropdown
                  triggerLabel={t("services")}
                  items={servicesItems}
                  isShrunk={isShrunk}
                />
              </li> */}

              {/* Commandes - Dropdown (visible si connecté) */}
              {userId && (
                <li className="hidden md:flex">
                  <NavDropdown
                    triggerLabel={t("quotation.ordersCount")}
                    items={ordersItems}
                    isShrunk={isShrunk}
                  />
                </li>
              )}

              {/* Recherche */}
              <li className="flex items-center">
                {showSearch ? (
                  <div className="flex items-center bg-white rounded-md px-2 py-1">
                    <Search size={16} className="text-gray-500" />
                    <input
                      type="text"
                      value={query}
                      onChange={handleSearch}
                      placeholder={t("search") || "Rechercher..."}
                      className="ml-1 text-sm outline-none border-none bg-transparent w-24 md:w-32"
                      autoFocus
                      aria-label={t("search") || "Rechercher"}
                    />
                    <button 
                      onClick={clearSearch}
                      className="ml-1 text-gray-500 hover:text-gray-700"
                      aria-label={t("close") || "Fermer"}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowSearch(true)}
                    className="p-2 hover:bg-white/10 rounded-md transition-colors"
                    aria-label={t("search") || "Rechercher"}
                  >
                    <Search size={20} color={isShrunk ? "black" : "white"} />
                  </button>
                )}
              </li>

              {/* Panier */}
              {connect && (
                <li>
                  <button
                    onClick={gotToCart}
                    className="flex items-center relative p-2 hover:bg-white/10 rounded-md transition-colors"
                    aria-label={`${t("cart") || "Panier"} (${number} articles)`}
                  >
                    <ShoppingCart color={isShrunk ? "black" : "white"} size={22} />
                    {number > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center font-medium">
                        {number > 99 ? '99+' : number}
                      </span>
                    )}
                  </button>
                </li>
              )}

              {/* Langue */}
              <li>
                <NavDropdown
                  triggerLabel={t("languages")}
                  items={languageItems}
                  isShrunk={isShrunk}
                  className="border rounded px-2 py-1"
                />
              </li>

              {/* Profil utilisateur */}
              <li>
                {connect ? (
                  <NavDropdown
                    triggerLabel={t("profile")}
                    items={userMenuItems}
                    isShrunk={isShrunk}
                  />
                ) : (
                  <button
                    onClick={() => navigate("/signup")}
                    className="flex items-center gap-1 md:gap-2 p-2 hover:bg-white/10 rounded-md transition-colors"
                    aria-label={t("sign_in") || "Se connecter"}
                  >
                    <CircleUser color={isShrunk ? "black" : "white"} size={22} />
                  </button>
                )}
              </li>

            </ul>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Navbar;